import * as React from 'react';
import { RightAngleLinkModel, DefaultLinkFactory } from '@projectstorm/react-diagrams';
import { LinkModel, LayerModel } from '@projectstorm/react-diagrams-core';

import logger from '@/utils/logger';
import { PipeLinkWidget } from './PipeLinkWidget';
import { token } from '@/theme';
import PIDEditStore from '@/store/PIDEditing/PIDEditStore';

export class PipeLinkModel extends RightAngleLinkModel {
  pipeEntity;

  constructor(pipeEntity) {
    super({
      type: 'pipe',
      width: 2,
    });
    this.pipeEntity = pipeEntity;
  }

  getEntity() {
    return this.pipeEntity;
  }
}

export class PipeLinkFactory extends DefaultLinkFactory<PipeLinkModel> {
  defaultColor;
  private _isEditMode: boolean;
  private stateChangeListeners: ((isEditMode: boolean) => void)[] = [];
  private _clickedPipeId: string | null = null;
  private clickedPipeListeners: ((pipeId: string | null) => void)[] = [];
  private deviceTypeChangeListeners: (() => void)[] = [];
  private onAddDeviceClick?: (pipeId: string, screenPosition: { x: number; y: number }) => void;
  public pidEditStore: PIDEditStore;

  constructor(
    isEditMode = false,
    onAddDeviceClick?: (pipeId: string, screenPosition: { x: number; y: number }) => void,
    pidEditStore?: PIDEditStore,
  ) {
    super();
    this.type = 'pipe';
    this.defaultColor = token.colorBorder;
    this._isEditMode = isEditMode;
    this.onAddDeviceClick = onAddDeviceClick;
    this.pidEditStore = pidEditStore;
  }

  // Method to subscribe to device type changes
  subscribeToDeviceTypeChange(listener: () => void) {
    this.deviceTypeChangeListeners.push(listener);
    return () => {
      this.deviceTypeChangeListeners = this.deviceTypeChangeListeners.filter(l => l !== listener);
    };
  }

  // Method to notify listeners of device type changes (called by store)
  notifyDeviceTypeChange() {
    this.deviceTypeChangeListeners.forEach(listener => listener());
  }

  get isEditMode(): boolean {
    return this._isEditMode;
  }

  set isEditMode(value: boolean) {
    this._isEditMode = value;
    this.stateChangeListeners.forEach(listener => listener(value));
  }

  get clickedPipeId(): string | null {
    return this._clickedPipeId;
  }

  set clickedPipeId(value: string | null) {
    this._clickedPipeId = value;
    this.clickedPipeListeners.forEach(listener => listener(value));
  }

  subscribeToClickedPipe(listener: (pipeId: string | null) => void) {
    this.clickedPipeListeners.push(listener);
    return () => {
      this.clickedPipeListeners = this.clickedPipeListeners.filter(l => l !== listener);
    };
  }

  bringLinkToFront(link: PipeLinkModel) {
    const model = this.engine.getModel();
    // The default link layer type is 'diagram-links'
    const linkLayer = model.getLayers().find(layer => layer.getOptions().type === 'diagram-links') as LayerModel<
      LinkModel
    >;

    if (linkLayer && linkLayer.getModel(link.getID())) {
      linkLayer.removeModel(link);
      linkLayer.addModel(link);
    }
  }

  setEditMode(value: boolean) {
    this.isEditMode = value;
    this.engine.repaintCanvas();
  }

  setClickedPipe(pipeId: string | null) {
    this.clickedPipeId = pipeId;

    if (pipeId) {
      const link = this.engine.getModel().getLink(pipeId) as PipeLinkModel;
      if (link) {
        this.bringLinkToFront(link as PipeLinkModel);
        this.pidEditStore?.setClickedPipe(link);
      }
    } else {
      this.pidEditStore?.setClickedPipe(null);
    }
    this.engine.repaintCanvas();
  }

  handleAddDeviceClick(pipeId: string, screenPosition: { x: number; y: number }) {
    this.setClickedPipe(pipeId);
    if (this.onAddDeviceClick) {
      this.onAddDeviceClick(pipeId, screenPosition);
    }
  }

  subscribeToStateChange(listener: (isEditMode: boolean) => void) {
    this.stateChangeListeners.push(listener);
    return () => {
      this.stateChangeListeners = this.stateChangeListeners.filter(l => l !== listener);
    };
  }

  generateModel(pipe): PipeLinkModel {
    logger('Pipe generated', { on: false, tags: ['yanai'] });
    return new PipeLinkModel(pipe);
  }

  generateReactWidget(event): JSX.Element {
    return (
      <PipeLinkWidget
        diagramEngine={this.engine}
        link={event.model}
        factory={this}
        color={event.model.color || this.defaultColor}
        editMode={this._isEditMode}
        token={token}
      />
    );
  }
}
